import { useMemo, useState } from 'react'
import Link from 'next/link'
import cn from 'classnames'

import Title from '../Title'
import TextBody from '../TextBody/TextBody'
import styles from './Datasheet.module.scss'
import QuestionmarkTooltip from '../QuestionmarkTooltip'

import { useDiscountContext } from '@context/discountContext'
import { getTrainingLabel } from '@/utils/format'
import { getAge, convertGender, getNumberOfFavorites } from './lib/utils'

export default function Datasheet({
  isMobile,
  dog,
  favs,
  breedDetails,
  isFavourite,
  updateFavourite,
  updatedData,
}) {
  const { registrations, bestInBreedDict, badgesAmount, trainingLabel } =
    useMemo(() => {
      const registrations =
        dog?.registrations?.split(',').filter((r) => !!r && r !== 'N/A') || []

      const trainingLabel = getTrainingLabel(dog?.training)

      const bestInBreedDict = {
        registration: {
          value: !!registrations?.length,
          label: `${registrations?.[0]} Registered`,
          image: 'registration.svg',
          alt: 'registration',
        },
        obedience: {
          value:
            !!dog?.training && (!!registrations?.length || !!dog?.bloodline),
          label: `${trainingLabel} Trained`,
          image: 'obedience.svg',
          alt: 'obedience',
        },
        bloodline: {
          value: !!dog?.bloodline,
          label: 'Champion Bloodline',
          image: 'champion.svg',
          alt: 'champion',
        },
      }

      return {
        registrations,
        bestInBreedDict,
        badgesAmount: Object.values(bestInBreedDict).filter((v) => v.value)
          .length,
        trainingLabel,
      }
    }, [dog])

  const { discount: contextDiscount } = useDiscountContext()
  const discount = useMemo(() => {
    const _dogDiscount = dog?.discount?.[0]?.amount || 0
    const _breedDiscount = dog?.breed?.discount?.[0]?.amount || 0

    const _discount =
      _dogDiscount > _breedDiscount
        ? dog?.discount?.[0]
        : dog?.breed?.discount?.[0] || contextDiscount

    if (_discount?.applyDiscountByDefault) return _discount

    return null
  }, [dog?.discount, dog?.breed?.discount, contextDiscount])

  const [truncateDescription, setTruncateDescription] = useState(true)

  return (
    <div className={styles.datasheet}>
      <div className={styles.favourite} onClick={updateFavourite}>
        <div
          className={cn(styles.heart, isFavourite ? styles.isFavourite : null)}
        />
      </div>

      <div className={styles.basics}>
        <div
          className={cn({
            [styles.containerName]: true && updatedData?.isAvailable,
            [styles.reservedPuppy]: !updatedData?.isAvailable,
            [styles.hasBanner]: !dog?.isAvailable,
          })}
        >
          <Title variant="h1">{dog?.name}</Title>

          {updatedData?.isAvailable ? (
            <div className={discount ? styles.priceWithDiscount : styles.price}>
              <div className={styles.priceContainer}>
                <div
                  className={
                    discount ? cn(styles.price, styles.discount) : styles.price
                  }
                >
                  {discount
                    ? `$${(dog?.salePrice?.subTotal - discount?.amount) / 100}`
                    : dog?.salePrice?.puppyPrice}
                </div>

                <div
                  className={styles.currency}
                  style={{ color: discount ? '#00ad66' : '#393939' }}
                >
                  {dog?.currency !== 'USD' && dog?.currency}
                </div>
              </div>

              <div className={styles.priceContainer}>
                {discount && (
                  <div className={styles.strike}>
                    {dog?.salePrice?.puppyPrice}{' '}
                    {dog?.currency !== 'USD' && dog?.currency}
                  </div>
                )}

                <QuestionmarkTooltip
                  anchorSelect="price-questions-tooltip"
                  content="Price excludes shipping. No hidden fees!"
                  place="top"
                />
              </div>
            </div>
          ) : (
            <div className={styles.imgSold} />
          )}
        </div>

        <div className={styles.secondLine}>
          <div className={styles.breed}>
            <div className={styles.dataBreed}>
              <div className={styles.line}>
                <Link href={`/search?breed=${dog?.breed?.id}`}>
                  <Title variant="h3">{dog?.breed?.name} </Title>
                </Link>

                {dog?.gender && (
                  <div
                    className={cn(styles.gender, {
                      [styles.boy]: dog?.gender === 'Male',
                      [styles.girl]: dog?.gender === 'Female',
                    })}
                  >
                    {convertGender(dog?.gender)}
                  </div>
                )}
              </div>

              {dog?.isAvailable && !!favs && (
                <div className={styles.availability}>
                  <div>{getNumberOfFavorites(favs)}</div>
                </div>
              )}
            </div>
          </div>

          {dog.isAvailable && (
            <Link href="/puppy-financing" className={styles.financing}>
              <span>Financing options</span> are available.
            </Link>
          )}
        </div>
      </div>

      {!!badgesAmount && (
        <div
          className={cn(styles.best, {
            [styles.row]: !isMobile || badgesAmount === 1,
            [styles.column]: isMobile && badgesAmount > 1,
          })}
        >
          <div className={styles.header}>
            <div className={styles.title}>
              <img
                src="/puppy/laureate-r.svg"
                alt="laureate"
                style={{ marginLeft: '4px' }}
              />

              <h2>Best in Breed</h2>

              <img
                src="/puppy/laureate-l.svg"
                alt="laureate"
                style={{ marginLeft: '4px' }}
              />
            </div>

            <TextBody size="s">
              This puppy meets the highest quality standards.
            </TextBody>
          </div>

          <div className={styles.badges}>
            {Object.keys(bestInBreedDict).map((k) => {
              const v = bestInBreedDict[k]

              if (v.value) {
                return (
                  <div className={styles.badge} key={`best-in-breed-${k}`}>
                    <img src={`/puppy/${v.image}`} alt={v.alt} />

                    <TextBody size="s">{v.label}</TextBody>
                  </div>
                )
              }

              return null
            })}
          </div>
        </div>
      )}

      <div className={styles.details}>
        <div className={styles.grid}>
          <div className={styles.col}>
            {dog?.litter?.birthDate && (
              <div className={styles.container}>
                <TextBody weight="bold" className={styles.label}>
                  Age
                </TextBody>
                <TextBody className={styles.data}>
                  {getAge(dog?.litter?.birthDate)}
                </TextBody>
              </div>
            )}

            {dog?.color && (
              <div className={styles.container}>
                <TextBody weight="bold" className={styles.label}>
                  Color
                </TextBody>

                <TextBody className={styles.data}>{dog?.color}</TextBody>
              </div>
            )}

            {dog?.litter?.mom?.weight && (
              <div className={styles.container}>
                <TextBody weight="bold" className={styles.label}>
                  Mom's Weight
                </TextBody>

                <TextBody className={styles.data}>
                  {dog?.litter?.mom?.weight} Lb
                </TextBody>
              </div>
            )}

            {dog?.litter?.dad?.weight && (
              <div className={styles.container}>
                <TextBody weight="bold" className={styles.label}>
                  Dad's Weight
                </TextBody>

                <TextBody className={styles.data}>
                  {dog?.litter?.dad?.weight} Lb
                </TextBody>
              </div>
            )}

            {!!registrations.length && registrations[0] !== 'N/A' && (
              <div className={styles.container}>
                <TextBody weight="bold" className={styles.label}>
                  Registrations
                </TextBody>

                <TextBody className={styles.data}>{registrations[0]}</TextBody>
              </div>
            )}

            <div className={styles.container}>
              <TextBody weight="bold" className={styles.label}>
                Puppy ID
              </TextBody>

              <TextBody className={styles.data}>{`${dog?.id}`}</TextBody>
            </div>
          </div>

          <div className={styles.col}>
            <div className={styles.container}>
              <TextBody weight="bold" className={styles.label}>
                Location
              </TextBody>

              <TextBody className={styles.data}>
                {dog?.breeder?.city}, {dog?.breeder?.state?.trim()}
              </TextBody>
            </div>

            <a href="#pick-and-travel" className={styles.travel}>
              <div className={styles.plane} />

              <div>
                <TextBody weight="bold">Pickup & Delivery available.</TextBody>

                {dog.isAvailable && (
                  <TextBody variant="span">Get a travel quote.</TextBody>
                )}
              </div>
            </a>
          </div>
        </div>

        {dog?.description && (
          <div className={styles.about}>
            <TextBody weight="bold">About {dog?.name}</TextBody>

            <TextBody
              className={cn({
                [styles.truncate]: truncateDescription,
              })}
            >
              {dog.description}
            </TextBody>

            <div
              className={styles.button}
              onClick={() => setTruncateDescription((curr) => !curr)}
            >
              <TextBody weight="bold" variant="span">
                View {truncateDescription ? 'more' : 'less'}
              </TextBody>

              <div
                className={cn(styles.arrow, {
                  [styles.right]: truncateDescription,
                  [styles.left]: !truncateDescription,
                })}
              />
            </div>
          </div>
        )}

        {(!!trainingLabel || breedDetails?.temperament) && (
          <div className={styles.breed}>
            {trainingLabel && (
              <div className={styles.fact}>
                <TextBody weight="bold">Training</TextBody>

                <TextBody>{`${trainingLabel} Training`}</TextBody>
              </div>
            )}

            {breedDetails?.temperament && (
              <div className={styles.fact}>
                <TextBody weight="bold">Breed Temperament</TextBody>

                <TextBody>{breedDetails.temperament}</TextBody>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
